import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert
} from 'reactstrap';
import axios from 'axios';
import api from '../../config';

const PartnerWithUsForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organization: '',
    title: '',
    phone: '',
    email: '',
    country: '',
    hearAboutUs: '',
    interestedIn: '',
    url: '',
    comment: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const messageRef = useRef(null);

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`api/list/country`);
        if (response.data.isOk) {
          setCountries(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    fetchCountries();
  }, []);

  const hearAboutUs = [
    { value: '', label: 'Select' },
    { value: 'Linkedin', label: 'Linkedin' },
    { value: 'Web search', label: 'Web search' },
    { value: 'Word of Mouth', label: 'Word of Mouth' },
    { value: 'Other', label: 'Other' }
  ];

  const interests = [
    { value: '', label: 'Select' },
    { value: 'Speed Networking', label: 'Speed Networking' },
    { value: 'Webinar', label: 'Webinar' },
    { value: 'Fitness/Sports Events', label: 'Fitness/Sports Events' },
    { value: 'Conference', label: 'Conference' },
    { value: 'Offsite', label: 'Offsite' },
    { value: 'Podcasts', label: 'Podcasts' },
    { value: 'Roundtables', label: 'Roundtables' },
    { value: 'Academic Workshops', label: 'Academic Workshops' }
  ];

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    if (name === 'phone') {
      // Allow only digits for the phone field
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.organization.trim()) newErrors.organization = 'Organization is required';
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.hearAboutUs) newErrors.hearAboutUs = 'Please select how you heard about us';
    if (!formData.interestedIn) newErrors.interestedIn = 'Please select your interest';
    if (!formData.url.trim()) newErrors.url = 'LinkedIn URL is required';

    // Enhanced email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
    }

    // Enhanced phone validation
    const phoneRegex = /^[\+]?[1-9][\d\s\-\(\)]{7,20}$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number with country code (e.g., +1234567890)';
    }

    // Enhanced LinkedIn URL validation
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/.*/;
    if (formData.url && !linkedinRegex.test(formData.url)) {
      newErrors.url = 'Please enter a valid LinkedIn URL (e.g., https://www.linkedin.com/in/username)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${api.API_URL}/api/auth/create/partner-with-us`, formData);

      if (response.data.isOk) {
        // Navigate to Thank You page
        navigate('/thank-you');
      } else {
        setMessage({
          type: 'danger',
          text: response.data.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setMessage({
        type: 'danger',
        text: error.response?.data?.message || 'Something went wrong. Please try again.'
      });

      // Scroll to message
      setTimeout(() => {
        messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="py-5" id="partner">
      <Row className="justify-content-center">
        <Col lg={12}>
          <h3 className="text-orange mb-4">We're Happy To Help You!</h3>

          {message.text && (
            <Alert color={message.type} ref={messageRef} className="mb-4">
              {message.text}
            </Alert>
          )}

          <Form className="contact-form" onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col lg={6} md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">First Name *</Label>
                  <Input
                    type="text"
                    name="firstName"
                    id="firstName"
                    className="form-control"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    invalid={!!errors.firstName}
                  />
                  {errors.firstName && <div className="invalid-feedback d-block">{errors.firstName}</div>}
                </FormGroup>
              </Col>

              <Col lg={6} md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Last Name *</Label>
                  <Input
                    type="text"
                    name="lastName"
                    id="lastName"
                    className="form-control"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    invalid={!!errors.lastName}
                  />
                  {errors.lastName && <div className="invalid-feedback d-block">{errors.lastName}</div>}
                </FormGroup>
              </Col>

              <Col md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Organization *</Label>
                  <Input
                    type="text"
                    name="organization"
                    id="organization"
                    className="form-control"
                    placeholder="Organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    invalid={!!errors.organization}
                  />
                  {errors.organization && <div className="invalid-feedback d-block">{errors.organization}</div>}
                </FormGroup>
              </Col>

              <Col md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Title *</Label>
                  <Input
                    type="text"
                    name="title"
                    id="title"
                    className="form-control"
                    placeholder="Title"
                    value={formData.title}
                    onChange={handleInputChange}
                    invalid={!!errors.title}
                  />
                  {errors.title && <div className="invalid-feedback d-block">{errors.title}</div>}
                </FormGroup>
              </Col>

              <Col md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Phone *</Label>
                  <Input
                    min={0}
                    onWheel={(e) => e.target.blur()}
                    type="text"
                    name="phone"
                    id="phone"
                    className="form-control"
                    placeholder="Phone number with Country code (e.g., +1234567890)"
                    value={formData.phone}
                    onChange={handleInputChange}
                    invalid={!!errors.phone}
                  />
                  {errors.phone && <div className="invalid-feedback d-block">{errors.phone}</div>}
                </FormGroup>
              </Col>

              <Col md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Email Id *</Label>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    className="form-control"
                    placeholder="Email Id (e.g., user@example.com)"
                    value={formData.email}
                    onChange={handleInputChange}
                    invalid={!!errors.email}
                  />
                  {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                </FormGroup>
              </Col>

              <Col md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Country *</Label>
                  <Input
                    type="select"
                    name="country"
                    id="country"
                    className="form-select"
                    value={formData.country}
                    onChange={handleInputChange}
                    invalid={!!errors.country}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country._id} value={country._id}>
                        {country.countryName}
                      </option>
                    ))}
                  </Input>
                  {errors.country && <div className="invalid-feedback d-block">{errors.country}</div>}
                </FormGroup>
              </Col>

              <Col md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Where did you hear about us *</Label>
                  <Input
                    type="select"
                    name="hearAboutUs"
                    id="hearAboutUs"
                    className="form-select"
                    value={formData.hearAboutUs}
                    onChange={handleInputChange}
                    invalid={!!errors.hearAboutUs}
                  >
                    {hearAboutUs.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                  {errors.hearAboutUs && <div className="invalid-feedback d-block">{errors.hearAboutUs}</div>}
                </FormGroup>
              </Col>

              <Col md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Interested in *</Label>
                  <Input
                    type="select"
                    name="interestedIn"
                    id="interestedIn"
                    className="form-select"
                    value={formData.interestedIn}
                    onChange={handleInputChange}
                    invalid={!!errors.interestedIn}
                  >
                    {interests.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                  {errors.interestedIn && <div className="invalid-feedback d-block">{errors.interestedIn}</div>}
                </FormGroup>
              </Col>

              <Col md={6} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Your LinkedIn URL *</Label>
                  <Input
                    type="url"
                    name="url"
                    id="url"
                    className="form-control"
                    placeholder="https://www.linkedin.com/in/username"
                    value={formData.url}
                    onChange={handleInputChange}
                    invalid={!!errors.url}
                  />
                  {errors.url && <div className="invalid-feedback d-block">{errors.url}</div>}
                </FormGroup>
              </Col>

              <Col xs={12} className='m-0'>
                <FormGroup>
                  <Label className="form-label">Comments</Label>
                  <Input
                    type="textarea"
                    name="comment"
                    id="comment"
                    className="form-control"
                    rows="5"
                    placeholder="Share your experience with us"
                    value={formData.comment}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>

              <Col xs={12}>
                <div className="g-recaptcha" data-sitekey="6LfMtKQrAAAAAGUwlybXGaxYDcD0p2TMqc7_S-E8">
                </div>
              </Col>

              <Col xs={12}>
                <Button
                  type="submit"
                  className="btn-partner"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Contact Me'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default PartnerWithUsForm;